import type { ReactNode } from 'react';
import type { ComponentProps } from 'react';
import { Box } from '@mui/material';
import { StyledSectionHeading } from './InputLabel';

type AccessibleTextAreaProps = {
  label: ReactNode;
  /** Id for the label and for the textarea (accessibility). */
  id: string;
  tooltip?: ReactNode;
  /** Number of visible rows. Uses native textarea (no TextareaAutosize) to avoid the hidden mirror that Wave flags. */
  rows?: number;
} & Omit<ComponentProps<'textarea'>, 'id' | 'aria-labelledby'>;

/**
 * Label (as span) + native textarea with correct accessibility (aria-labelledby).
 * Use tooltip for help icons so screen readers only announce the tooltip when focusing the icon, not the textarea.
 */
export function AccessibleTextArea({
  label,
  id,
  tooltip,
  rows = 4,
  ...textareaProps
}: AccessibleTextAreaProps) {
  const labelId = `${id}-label`;
  const labelTextId = `${id}-label-text`;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <StyledSectionHeading id={labelId}>
          <span id={labelTextId}>{label}</span>
        </StyledSectionHeading>
        {tooltip}
      </Box>
      <textarea
        {...textareaProps}
        id={id}
        rows={rows}
        aria-labelledby={labelTextId}
        className={['jst-textarea', textareaProps.className]
          .filter(Boolean)
          .join(' ')}
      />
    </>
  );
}
